import { calculateUnlocks, type CosmeticUnlock } from './Progression'
import { COSMETICS, type ShellId, type TrailId } from '../content/CosmeticCatalog'

export type ThemeId = 'ion' | 'reactor' | 'nova' | 'ghost'
export type QualityPreference = 'auto' | 'high' | 'medium' | 'low'

export interface LocalProfile {
  readonly schemaVersion: 2
  readonly bestDistance: number
  readonly theme: ThemeId
  readonly shell: ShellId
  readonly trail: TrailId
  readonly quality: QualityPreference
  readonly reducedMotion: boolean
  readonly highContrast: boolean
  readonly bloom: boolean
  readonly muted: boolean
  readonly musicVolume: number
  readonly sfxVolume: number
  readonly dragSteering: boolean
  readonly rivalsEnabled: boolean
  readonly onlineEnabled: boolean
  readonly mastery: Readonly<Record<string, number>>
  readonly unlocks: readonly CosmeticUnlock[]
}

const STORAGE_KEY = 'neon-descent-profile'

export function createDefaultProfile(): LocalProfile {
  return {
    schemaVersion: 2,
    bestDistance: 0,
    theme: 'ion',
    shell: 'ion-core',
    trail: 'ion',
    quality: 'auto',
    reducedMotion: false,
    highContrast: false,
    bloom: true,
    muted: false,
    musicVolume: 0.55,
    sfxVolume: 0.8,
    dragSteering: false,
    rivalsEnabled: false,
    onlineEnabled: false,
    mastery: {},
    unlocks: ['ion'],
  }
}

function isProfile(value: unknown): value is LocalProfile {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<LocalProfile>
  return candidate.schemaVersion === 2
    && typeof candidate.bestDistance === 'number'
    && Number.isFinite(candidate.bestDistance)
    && ['ion', 'reactor', 'nova', 'ghost'].includes(candidate.theme ?? '')
    && ['auto', 'high', 'medium', 'low'].includes(candidate.quality ?? '')
    && typeof candidate.reducedMotion === 'boolean'
    && typeof candidate.highContrast === 'boolean'
    && typeof candidate.bloom === 'boolean'
    && typeof candidate.muted === 'boolean'
    && typeof candidate.musicVolume === 'number'
    && Number.isFinite(candidate.musicVolume)
    && typeof candidate.sfxVolume === 'number'
    && Number.isFinite(candidate.sfxVolume)
    && typeof candidate.dragSteering === 'boolean'
    && typeof candidate.rivalsEnabled === 'boolean'
    && typeof candidate.onlineEnabled === 'boolean'
    && COSMETICS.shells.some(({ id }) => id === candidate.shell)
    && COSMETICS.trails.some(({ id }) => id === candidate.trail)
    && typeof candidate.mastery === 'object'
    && candidate.mastery !== null
    && !Array.isArray(candidate.mastery)
}

interface LegacyProfile {
  readonly schemaVersion: 1
  readonly bestDistance: number
  readonly theme: ThemeId
  readonly quality: QualityPreference
  readonly reducedMotion: boolean
  readonly highContrast: boolean
  readonly bloom: boolean
  readonly muted: boolean
  readonly musicVolume: number
  readonly sfxVolume: number
  readonly dragSteering: boolean
}

function isLegacyProfile(value: unknown): value is LegacyProfile {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Partial<LegacyProfile>
  return candidate.schemaVersion === 1
    && typeof candidate.bestDistance === 'number'
    && ['ion', 'reactor', 'nova', 'ghost'].includes(candidate.theme ?? '')
    && ['auto', 'high', 'medium', 'low'].includes(candidate.quality ?? '')
    && typeof candidate.reducedMotion === 'boolean'
    && typeof candidate.highContrast === 'boolean'
    && typeof candidate.bloom === 'boolean'
    && typeof candidate.muted === 'boolean'
    && typeof candidate.musicVolume === 'number'
    && typeof candidate.sfxVolume === 'number'
    && typeof candidate.dragSteering === 'boolean'
}

function migrateLegacy(profile: LegacyProfile): LocalProfile {
  const bestDistance = Math.max(0, profile.bestDistance)
  return {
    ...profile,
    schemaVersion: 2,
    bestDistance,
    shell: bestDistance >= 3_000 ? 'facet' : 'ion-core',
    trail: bestDistance >= 500 ? 'pulse' : 'ion',
    rivalsEnabled: false,
    onlineEnabled: false,
    mastery: {},
    unlocks: calculateUnlocks(bestDistance),
  }
}

export class ProfileRepository {
  private readonly storage: Storage | null

  public constructor(storage: Storage | null = typeof localStorage === 'undefined' ? null : localStorage) {
    this.storage = storage
  }

  public load(): LocalProfile {
    try {
      const raw = this.storage?.getItem(STORAGE_KEY)
      if (raw === null || raw === undefined) return createDefaultProfile()
      const parsed: unknown = JSON.parse(raw)
      if (isLegacyProfile(parsed)) return migrateLegacy(parsed)
      if (!isProfile(parsed)) return createDefaultProfile()
      const bestDistance = Math.max(0, parsed.bestDistance)
      const mastery = Object.fromEntries(Object.entries(parsed.mastery)
        .filter(([, value]) => typeof value === 'number' && Number.isFinite(value) && value >= 0))
      return {
        ...parsed,
        bestDistance,
        musicVolume: Math.max(0, Math.min(1, parsed.musicVolume)),
        sfxVolume: Math.max(0, Math.min(1, parsed.sfxVolume)),
        mastery,
        unlocks: calculateUnlocks(bestDistance),
      }
    } catch {
      return createDefaultProfile()
    }
  }

  public save(profile: LocalProfile): boolean {
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(profile))
      return this.storage !== null
    } catch {
      return false
    }
  }
}
